import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import Cancel from "../../components/Atomos/Icons/Close";
import Success from "../../components/Atomos/Icons/Success";
import Folder from "../../components/Atomos/Icons/EditMode/Folder";
import Trash from "../../components/Atomos/Icons/EditMode/Trash";
import ImageCropper from "../../components/Organismos/ImageCropper/ImageCropper";
import IFirm from "../../interfaces/models/IFirm";
import { getFirmById, updateFirm } from "../../Services/Firm";
import { uploadImage } from "../../utils/UploadImage";
import { toast } from "react-toastify";

type FirmLogoModalProps = {
  isOpen: boolean;
  firmId: string;
  firmName: string;
  onClose: () => void;
};

type LogoPending = string | File | null;

const LEGACY_BUTTON_CLASS = "!w-auto shrink-0";

function resolveSavedUrl(value: string | undefined): string | null {
  const trimmed = String(value ?? "").trim();
  return trimmed || null;
}

function resolvePendingUrl(
  pending: LogoPending,
  saved: string | undefined,
): string | null {
  if (pending instanceof File) {
    return null;
  }
  if (pending === "") {
    return null;
  }
  if (pending !== null) {
    return pending.trim() || null;
  }
  return resolveSavedUrl(saved);
}

const FirmLogoModal: React.FC<FirmLogoModalProps> = ({
  isOpen,
  firmId,
  firmName,
  onClose,
}) => {
  const [firm, setFirm] = useState<IFirm | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingLogo, setPendingLogo] = useState<LogoPending>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [draggedPhoto, setDraggedPhoto] = useState<string | File | null>(null);
  const cropPhotoRef = useRef<File | null>(null);
  const cropSaveRef = useRef<(() => void) | null>(null);
  const [logoObjectUrl, setLogoObjectUrl] = useState<string | null>(null);

  const hasChanges = pendingLogo !== null;

  const resetState = useCallback(() => {
    setFirm(null);
    setLoading(false);
    setSaving(false);
    setError(null);
    setPendingLogo(null);
    setIsCropping(false);
    setDraggedPhoto(null);
    cropPhotoRef.current = null;
    cropSaveRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen || !firmId.trim()) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPendingLogo(null);
    setIsCropping(false);

    void getFirmById(firmId.trim())
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        if (!loaded) {
          setError("Could not load firm logo.");
          setFirm(null);
          return;
        }
        setFirm(loaded);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load firm logo.");
          setFirm(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, firmId]);

  useEffect(() => {
    if (pendingLogo instanceof File) {
      const url = URL.createObjectURL(pendingLogo);
      setLogoObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setLogoObjectUrl(null);
    return undefined;
  }, [pendingLogo]);

  const logoDisplayUrl =
    logoObjectUrl ?? resolvePendingUrl(pendingLogo, firm?.logo) ?? null;

  const handleSetCropPhoto = (photo: File | null) => {
    cropPhotoRef.current = photo;
  };

  const openCrop = () => {
    setDraggedPhoto(logoDisplayUrl ?? null);
    cropPhotoRef.current = null;
    setIsCropping(true);
    setError(null);
  };

  const handleCropComplete = () => {
    const file = cropPhotoRef.current;
    if (!file) {
      return;
    }

    setPendingLogo(file);
    setIsCropping(false);
    setDraggedPhoto(null);
    cropPhotoRef.current = null;
    cropSaveRef.current = null;
  };

  const handleCropBack = () => {
    setIsCropping(false);
    setDraggedPhoto(null);
    cropPhotoRef.current = null;
    cropSaveRef.current = null;
  };

  const handleDiscardChanges = () => {
    setPendingLogo(null);
    setError(null);
  };

  const handleDeleteLogo = () => {
    setPendingLogo("");
    setError(null);
  };

  const handleSave = async () => {
    if (!firm || !hasChanges) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let logoUrl = firm.logo ?? "";

      if (pendingLogo instanceof File) {
        const uploaded = await uploadImage(
          pendingLogo,
          firm.id,
          "firm/logos/",
        );
        if (!uploaded) {
          throw new Error("Failed to upload logo.");
        }
        logoUrl = uploaded;
      } else if (pendingLogo === "") {
        logoUrl = "";
      } else if (pendingLogo !== null) {
        logoUrl = pendingLogo;
      }

      const updated = await updateFirm({
        ...firm,
        logo: logoUrl,
      });

      setFirm(updated);
      setPendingLogo(null);
      toast.success("Firm logo updated.");
    } catch {
      setError("Could not save firm logo.");
      toast.error("Could not save firm logo.");
    } finally {
      setSaving(false);
    }
  };

  const baseTitle = firmName.trim()
    ? `Firm Logo — ${firmName.trim()}`
    : "Firm Logo";
  const title = isCropping
    ? `Change Logo — ${firmName.trim() || "Firm"}`
    : baseTitle;

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={handleClose}
      title={title}
      compact
      compactBodyScrolls={isCropping}
      className="max-w-2xl w-full"
    >
      <div className="flex flex-col gap-4">
        {loading ? (
          <p className="text-base text-gray-font">Loading…</p>
        ) : error && !firm ? (
          <p className="text-base text-red-600">{error}</p>
        ) : isCropping ? (
          <div className="flex flex-col gap-4">
            <ImageCropper
              setPhoto={handleSetCropPhoto}
              onHandleUpload={() => {}}
              draggedPhoto={draggedPhoto}
              onImageUpload={handleCropComplete}
              shape="rect"
              adaptCropToImage
              hideSaveButton
              onRegisterSave={(save) => {
                cropSaveRef.current = save;
              }}
            />
            <div className="flex flex-wrap justify-end gap-3 border-t border-neutral-200 pt-4">
              <LegacyButton
                text="Back"
                style="outlined"
                size="medium"
                content="textIcon"
                icon={<Cancel />}
                onClick={handleCropBack}
                color="neutral"
                className={LEGACY_BUTTON_CLASS}
              />
              <LegacyButton
                text="Apply"
                icon={<Success />}
                style="filled"
                size="medium"
                content="textIcon"
                onClick={() => cropSaveRef.current?.()}
                color="accent"
                className={LEGACY_BUTTON_CLASS}
              />
            </div>
          </div>
        ) : firm ? (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex h-[240px] w-full items-center justify-center rounded-lg border border-dashed border-[#BEBDBD] bg-[#F4F4F4] p-2">
                {logoDisplayUrl ? (
                  <img
                    src={logoDisplayUrl}
                    alt="Firm logo"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-sm italic text-gray-500">No logo</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <LegacyButton
                  text="Change"
                  icon={<Folder />}
                  style="filled"
                  size="medium"
                  content="textIcon"
                  onClick={openCrop}
                  color="accent"
                  disabled={saving}
                  className={LEGACY_BUTTON_CLASS}
                />
                <LegacyButton
                  text="Delete"
                  icon={<Trash />}
                  style="outlined"
                  size="medium"
                  content="textIcon"
                  onClick={handleDeleteLogo}
                  color="neutral"
                  disabled={saving || !logoDisplayUrl}
                  className={LEGACY_BUTTON_CLASS}
                />
              </div>
            </div>

            {error ? <p className="text-sm text-red-600">{error}</p> : null}

            <div className="mt-2 flex flex-wrap justify-end gap-3 border-t border-neutral-200 pt-4">
              {hasChanges ? (
                <>
                  <LegacyButton
                    text="Cancel"
                    icon={<Cancel />}
                    style="outlined"
                    size="medium"
                    content="textIcon"
                    onClick={handleDiscardChanges}
                    color="neutral"
                    disabled={saving}
                    className={LEGACY_BUTTON_CLASS}
                  />
                  <LegacyButton
                    text="Save"
                    icon={<Success />}
                    style="filled"
                    size="medium"
                    content="textIcon"
                    onClick={() => void handleSave()}
                    color="accent"
                    disabled={saving || !hasChanges}
                    isLoading={saving}
                    className={LEGACY_BUTTON_CLASS}
                  />
                </>
              ) : (
                <LegacyButton
                  text="Close"
                  style="outlined"
                  size="medium"
                  content="text"
                  onClick={handleClose}
                  color="neutral"
                  disabled={saving}
                  className={LEGACY_BUTTON_CLASS}
                />
              )}
            </div>
          </>
        ) : null}

        {!loading && !firm && !isCropping ? (
          <div className="mt-2 flex flex-wrap justify-end gap-3 border-t border-neutral-200 pt-4">
            <LegacyButton
              text="Close"
              style="outlined"
              size="medium"
              content="text"
              onClick={handleClose}
              color="neutral"
              className={LEGACY_BUTTON_CLASS}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default FirmLogoModal;
